"use client";

import { createContext, use, useEffect } from "react";

type KeyboardStepActionContextValue = {
  registerKeyboardStepAction: (action: (() => void) | null) => void;
};

const KeyboardStepActionContext = createContext<KeyboardStepActionContextValue>(
  {
    registerKeyboardStepAction: () => {},
  },
);

/**
 * Registers `action` as the form's keyboard-advance action — what LeadForm's
 * `handleSubmit` runs in place of submitting. Pass null when this step must
 * not handle the browser's implicit submission.
 */
function useRegisterKeyboardStepAction(action: (() => void) | null): void {
  const { registerKeyboardStepAction } = use(KeyboardStepActionContext);
  useEffect(() => {
    // A step passing null registers nothing rather than writing null: effect
    // setups run in document order, so a later opted-out step would otherwise
    // clobber the active step. React runs every cleanup for a commit before
    // any setup, so the outgoing step's cleanup has already cleared the slot.
    if (!action) {
      return;
    }
    registerKeyboardStepAction(action);
    return () => registerKeyboardStepAction(null);
  }, [action, registerKeyboardStepAction]);
}

export { KeyboardStepActionContext, useRegisterKeyboardStepAction };
